<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="update-tyre-popup" v-if="popupOpen">
    <div v-if="!returnReviewOpen">
      <TableBox
        :tableData="retreadTyreListFiltered"
        title="Retread Tyres List"
        :maxSelection="-1"
        :selectedRow.sync="retreadTyresSelectedRow"
        :hideColumns="updateTyreStatusTableHideColumns"
        :dataType="updateTyreStatusTableDataType"
      />
      <div class="cta-group">
        <div class="w-100">
          <Dropdown
            placeholder="Update Status"
            :selectedObject.sync="selectedAction"
            :items="actionList"
            itemKey="label"
          />
          <Dropdown
            v-if="selectedAction && selectedAction.value === 'warehouse'"
            placeholder="Warehouse"
            :selectedObject.sync="selectedWarehouse"
            :errorMessage.sync="warehouseError"
            @selected="warehouseError = ''"
            :items="warehouseList"
            itemKey="label"
          />
          <div v-if="updateErrorMessage" style="color: red" role="alert">{{ displayUpdateError }}</div>
        </div>
        <Button :disabled="updateLoading" @click="updateClicked">Update</Button>
        <Button :disabled="updateLoading" @click="$emit('update:popupOpen', false)">Close</Button>
      </div>
    </div>
    <div v-else class="return-review" role="dialog" aria-modal="true" aria-labelledby="retread-return-title">
      <div class="table-box-main return-edit-table">
        <div class="top-block">
          <div class="right-section">
            <div class="title" id="retread-return-title" ref="returnTitle" tabindex="-1">Receive Retread Tyres</div>
          </div>
          <div class="left-section">{{ returnItems.length }} records</div>
        </div>
        <div v-if="updateErrorMessage" style="color: red" role="alert">{{ displayUpdateError }}</div>
        <div class="table-block" role="table" aria-labelledby="retread-return-title">
          <div class="table-header blue-theme" role="row">
            <div class="table-column" role="columnheader">#</div>
            <div class="table-column" role="columnheader">Serial No</div>
            <div class="table-column" role="columnheader">Current Part Type</div>
            <div class="table-column" role="columnheader">Part Type</div>
            <div class="table-column" role="columnheader">Unit Price</div>
          </div>
          <div v-for="(item, index) in returnItems" :key="item.tyreguid" class="table-row blue-theme-row" role="row">
            <div class="row-data" role="cell">{{ index + 1 }}</div>
            <div class="row-data" role="cell" :title="item.serialNo">{{ item.serialNo }}</div>
            <div class="row-data" role="cell" :title="item.currentPartType">{{ item.currentPartType }}</div>
            <div class="row-data" role="cell">
              <InputQuery
                placeholder="Part Type"
                :title="`Part Type (${item.serialNo})`"
                :selectedObject.sync="item.partType"
                :errorMessage.sync="item.partTypeError"
                :items="partTypeList"
                itemKey="label"
                :tableBoxHideColumns="partTypeHideColumns"
                :tableBoxDataType="partTypeDataType"
                :isDisabled="updateLoading"
                @selected="returnPartTypeSelected(item, $event)"
              />
            </div>
            <div class="row-data" role="cell">
              <Input
                placeholder="Unit Price"
                :title="`Unit Price (${item.serialNo})`"
                :inputValue.sync="item.unitPrice"
                :errorMessage.sync="item.unitPriceError"
                @focusChanged="unitPriceFocusChanged($event, item)"
                validator="currency"
                textAlign="right"
                :isDisabled="updateLoading"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="cta-group return-bulk-fields">
        <InputQuery
          placeholder="Part Type"
          :selectedObject.sync="bulkPartType"
          :items="partTypeList"
          itemKey="label"
          :tableBoxHideColumns="partTypeHideColumns"
          :tableBoxDataType="partTypeDataType"
          :isDisabled="updateLoading"
          @selected="bulkPartTypeSelected"
        />
        <Input
          placeholder="Unit Price"
          :inputValue.sync="bulkUnitPrice"
          @focusChanged="unitPriceFocusChanged($event)"
          validator="currency"
          textAlign="right"
          :isDisabled="updateLoading"
        />
        <Button :disabled="updateLoading || !bulkPartType" @click="applyToAll">Apply to All</Button>
      </div>
      <div class="cta-group">
        <div class="w-100">
          <Dropdown
            placeholder="Warehouse"
            :selectedObject="selectedWarehouse"
            :errorMessage.sync="warehouseError"
            :items="warehouseList"
            itemKey="label"
            :isDisabled="true"
          />
        </div>
        <Button :disabled="updateLoading" @click="receiveClicked">Send to Warehouse</Button>
        <Button :disabled="updateLoading" @click="returnReviewOpen = false">Back</Button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  computed: {
    displayUpdateError() {
      return String(this.updateErrorMessage || '').trim().replace(/\.+$/, '');
    },
    retreadTyreListFiltered() {
      return this.retreadTyreList.map((item, index) => {
        const {
          TyreGuid,
          SerialNo,
          NoOfRetread,
          TyreType,
          TyreIndex,
          JobNo,
          DateChange,
          DateUpdated,
          UserID,
          WhproductTxnid,
        } = item;

        return {
          '#': index + 1,
          'Serial No': SerialNo,
          'Tyre Type': TyreType,
          TyreIndex,
          'No Of Retread': NoOfRetread,
          'Job No': JobNo,
          'Date Change': DateChange,
          'Date Updated': DateUpdated,
          'User ID': UserID,
          'ProdTxnID': WhproductTxnid,
          TyreGuid,
        };
      });
    },
  },
  data: function () {
    return {
      retreadTyreList: [],
      retreadTyresSelectedRow: [],

      actionList: [
        { value: 'warehouse', label: 'Send to Warehouse' },
        { value: 'discard', label: 'Discard' },
      ],
      selectedAction: { value: 'warehouse', label: 'Send to Warehouse' },
      selectedWarehouse: null,
      warehouseError: '',
      warehouseList: [],
      returnReviewOpen: false,
      returnItems: [],
      partTypeList: [],
      partTypeHideColumns: ['label', 'Active', 'ProdTxnID', 'Unique Code', 'Expense Code', 'IsExpenses'],
      partTypeDataType: {
        '#': 'number',
        'Part Type': 'string',
        Description: 'string',
        'Part Group': 'string',
        'Unique Code': 'string',
        'Expense Code': 'string',
        IsExpenses: 'string',
        'Unit Price': 'currency',
        SuppCode: 'string',
        Spp_Name: 'string',
        Active: 'string',
        'Product Code': 'string',
        ProdName: 'string',
        Size: 'string',
        Shade: 'string',
        ProdTxnID: 'number',
        label: 'string',
      },
      bulkPartType: null,
      bulkUnitPrice: '',

      updateErrorMessage: '',
      updateLoading: false,

      updateTyreStatusTableHideColumns: ['TyreGuid'],
      updateTyreStatusTableDataType: {
        '#': 'number',
        'Serial No': 'string',
        'Tyre Type': 'string',
        TyreIndex: 'number',
        'No Of Retread': 'number',
        'Job No': 'string',
        'Date Change': 'date',
        'Date Updated': 'datetime',
        'User ID': 'string',
        'ProdTxnID': 'number',
        TyreGuid: 'string',
      },
    };
  },
  props: {
    popupOpen: { type: Boolean, default: false },
  },
  methods: {
    async updateClicked() {
      if (this.updateLoading) return;
      this.updateErrorMessage = '';
      this.warehouseError = '';

      let error = false;
      if (this.retreadTyresSelectedRow.length < 1) {
        this.updateErrorMessage = 'Select at least one tyre to update status';
        error = true;
      }

      if (!this.selectedAction) {
        this.updateErrorMessage = 'Select an Update Status';
        error = true;
      } else if (this.selectedAction.value == 'warehouse' && !this.selectedWarehouse) {
        this.warehouseError = 'Warehouse is required';
        error = true;
      }

      if (error) return;

      if (this.selectedAction.value === 'warehouse') {
        this.updateLoading = true;
        this.$fullscreenLoading('Getting Return Part Types');
        try {
          const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTTYPEWITHPRODUCTINFO', module: 'RETREADRETURN' });
          if (!Array.isArray(result)) throw new Error('Unable to load Part Types, please try again');
          this.partTypeList = result
            .filter((item) => item.Active == '1' && String(item['Part Group'] || '').trim().toUpperCase() === 'TYRE' && item.ProdTxnID)
            .map((item) => ({ ...item, label: `${item['Part Type']} - ${item.Description}` }));
          if (!this.partTypeList.length) throw new Error('No active tyre Part Types are available for warehouse return');
          this.returnItems = this.retreadTyresSelectedRow.map((selected) => {
            const tyre = this.retreadTyreList.find((item) => item.TyreGuid === selected.TyreGuid);
            if (!tyre) throw new Error('Selected tyres have changed, please refresh and select them again');
            const partType = this.partTypeList.find((item) => item.ProdTxnID == tyre.WhproductTxnid) || null;
            const previousUnitPrice = tyre.UnitPrice === null || tyre.UnitPrice === undefined ? '' : this.$currencyFormat(tyre.UnitPrice);
            const masterPrice = partType && partType['Unit Price'];
            return {
              tyreguid: tyre.TyreGuid,
              serialNo: tyre.SerialNo,
              currentPartType: tyre.TyreType,
              partType,
              previousUnitPrice,
              partTypeError: '',
              unitPriceError: '',
              unitPrice: masterPrice === null || masterPrice === undefined || String(masterPrice).trim() === '' ? previousUnitPrice : this.$currencyFormat(masterPrice),
            };
          });
          this.bulkPartType = null;
          this.bulkUnitPrice = '';
          this.returnReviewOpen = true;
          await this.$nextTick();
          if (this.$refs.returnTitle) this.$refs.returnTitle.focus();
        } catch (error) {
          this.updateErrorMessage = error.message || 'Unable to prepare the warehouse return, please try again';
        } finally {
          this.updateLoading = false;
          this.$fullscreenLoading();
        }
        return;
      }

      await this.saveUpdate();
    },

    returnPartTypeSelected(item, partType) {
      item.partTypeError = '';
      item.unitPriceError = '';
      const price = partType && partType['Unit Price'];
      item.unitPrice = price === null || price === undefined || String(price).trim() === '' ? item.previousUnitPrice : this.$currencyFormat(price);
    },

    bulkPartTypeSelected(partType) {
      const price = partType && partType['Unit Price'];
      this.bulkUnitPrice = price === null || price === undefined || String(price).trim() === '' ? '' : this.$currencyFormat(price);
    },

    unitPriceFocusChanged(focused, item) {
      if (focused) return;
      const value = item ? item.unitPrice : this.bulkUnitPrice;
      if (!value.trim()) return;
      const price = this.$currencyToNumber(value);
      if (price === null || !Number.isFinite(price)) return;
      if (item) item.unitPrice = this.$currencyFormat(price);
      else this.bulkUnitPrice = this.$currencyFormat(price);
    },

    applyToAll() {
      if (this.updateLoading || !this.bulkPartType) return;
      this.returnItems.forEach((item) => {
        item.partType = this.bulkPartType;
        item.partTypeError = '';
        item.unitPriceError = '';
        item.unitPrice = this.bulkUnitPrice.trim() === '' ? item.previousUnitPrice : this.bulkUnitPrice;
      });
      this.updateErrorMessage = '';
    },

    async receiveClicked() {
      if (this.updateLoading) return;
      this.updateErrorMessage = '';
      this.warehouseError = '';
      if (!this.selectedWarehouse) {
        this.warehouseError = 'Warehouse is required';
        return;
      }
      if (!this.returnItems.length) {
        this.updateErrorMessage = 'Select at least one tyre to send to warehouse';
        return;
      }
      const returnItems = [];
      let hasError = false;
      for (const item of this.returnItems) {
        item.partTypeError = '';
        item.unitPriceError = '';
        if (!item.partType || !this.partTypeList.some((part) => part.ProdTxnID == item.partType.ProdTxnID)) {
          item.partTypeError = 'Part Type is required';
          hasError = true;
        }
        const unitPrice = this.$currencyToNumber(item.unitPrice);
        if (!item.unitPrice.trim() || unitPrice === null || !Number.isFinite(unitPrice) || unitPrice < 0) {
          item.unitPriceError = !item.unitPrice.trim() ? 'Unit Price is required' : 'Enter a valid Unit Price of 0 or more';
          hasError = true;
        }
        if (!item.partTypeError && !item.unitPriceError) {
          returnItems.push({ tyreguid: item.tyreguid, prodtxnid: item.partType.ProdTxnID, unitprice: unitPrice });
        }
      }
      if (hasError) return;
      await this.saveUpdate(returnItems);
    },

    async saveUpdate(returnItems) {
      if (this.updateLoading) return;
      const selectedTyreGUID = this.retreadTyresSelectedRow.map((item) => {
        return item.TyreGuid;
      });

      const selectedAction = this.selectedAction.value;
      const selectedWarehouse = this.selectedWarehouse ? this.selectedWarehouse['Warehouse Code'] : '';

      this.updateLoading = true;
      this.$fullscreenLoading('Updating Retread Tyres');
      try {
        const response = await this.$apiCallV2('/WorkshopJob/UpdateRetreadTyre', {
          action: selectedAction,
          whsecode: selectedWarehouse,
          tyreguids: selectedTyreGUID,
          returnitems: returnItems,
        });

        const result = Array.isArray(response) ? response[0] : response;
        if (!result || result.Results !== '0') {
          this.updateErrorMessage = (result && result.ErrMsg) || 'Failed to update retread tyres';
          return;
        }

        const message = result && result.ErrMsg ? result.ErrMsg : 'Retread tyre status updated successfully';
        this.$showAlert('success', 'Update Successful', message);
        this.retreadTyresSelectedRow = [];
        this.clearFields();
        await this.getRetreadTyreList();
      } catch (error) {
        this.updateErrorMessage = error.message || 'Unable to update retread tyres, please refresh the list before retrying';
      } finally {
        this.updateLoading = false;
        this.$fullscreenLoading();
      }
    },

    async getRetreadTyreList() {
      const result = await this.$apiCallV2('/WorkshopJob/GetRetreadTyreList', {});
      this.retreadTyreList = [];
      await this.$nextTick();
      this.retreadTyreList = result ?? [];
    },

    async getWarehouse() {
      this.warehouseList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'WAREHOUSE' })).map(
        (warehouse) => {
          return {
            ...warehouse,
            label: `${warehouse['Warehouse Code']} - ${warehouse.Name}`,
          };
        },
      );
    },

    async apiGets() {
      this.$fullscreenLoading('Getting Retread Tyres');
      await Promise.all([this.getRetreadTyreList(), this.getWarehouse()]);
      this.$fullscreenLoading();
    },

    clearFields() {
      this.selectedAction = this.actionList.find((item) => item.value == 'warehouse') || null;
      this.selectedWarehouse = null;
      this.warehouseError = '';
      this.returnReviewOpen = false;
      this.returnItems = [];
      this.bulkPartType = null;
      this.bulkUnitPrice = '';
      this.updateErrorMessage = '';
    },
  },
  watch: {
    popupOpen(val) {
      this.clearFields();
      this.retreadTyresSelectedRow = [];
      if (val) this.apiGets();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.update-tyre-popup {
  position: fixed;
  z-index: 9999;
  background: rgba(0, 0, 0, 50%);
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  > * {
    height: 100%;
    max-height: 32rem;
    width: 100%;
    max-width: 55rem;
    background-color: white;
    border-radius: 4px;
    padding: 8px;
    overflow: auto;

    display: flex;
    flex-direction: column;

    > .table-box-main {
      min-height: 0;

      margin-bottom: 8px;
    }

    .cta-group {
      display: flex;
      align-items: end;
      gap: 4px;
      background-color: white;
      z-index: 2;
      button {
        height: 28px;
      }
    }
  }
  .return-edit-table {
    height: 100%;
    display: flex;
    flex-direction: column;
    > .top-block {
      display: flex;
      align-items: center;
      min-height: 40px;
      > .right-section {
        flex-grow: 1;
        > .title {
          font-weight: bold;
          font-size: 1.2em;
          margin-right: 10px;
        }
      }
      > .left-section {
        text-align: right;
        line-height: 1.3;
      }
    }
    > .table-block {
      overflow: auto;
      min-height: 0;
      flex: 1;
    }
    .table-header, .table-row {
      display: flex;
      width: 100%;
      min-width: 800px;
      > * {
        box-sizing: border-box;
        flex-shrink: 0;
      }
      > :nth-child(1) {
        width: 40px;
      }
      > :nth-child(2) {
        width: 140px;
      }
      > :nth-child(3) {
        width: 160px;
      }
      > :nth-child(4) {
        flex: 1;
        min-width: 300px;
      }
      > :nth-child(5) {
        width: 160px;
      }
    }
    .table-header {
      position: sticky;
      top: 0;
      z-index: 11;
      > .table-column {
        padding: 5px;
        border-right: 1px solid white;
        white-space: nowrap;
      }
    }
    .table-row > .row-data {
      display: flex;
      align-items: center;
      height: 32px;
      padding: 1px 3px;
      border-right: 1px solid rgba(128, 128, 128, 0.5);
      overflow: hidden;
      white-space: nowrap;
      &:nth-child(4), &:nth-child(5) {
        overflow: visible;
      }
      > .input-query-main, > .input-main {
        width: 100%;
        min-width: 0;
      }
    }
  }
  .return-review > .cta-group {
    z-index: auto;
    flex-wrap: wrap;
    > .w-100 {
      flex: 1 1 300px;
      min-width: 0;
    }
  }
  .return-bulk-fields {
    display: flex;
    flex-wrap: wrap;
    align-items: end;
    gap: 4px;
    margin-bottom: 4px;
    padding: 8px;
    border: 1px solid rgba(128, 128, 128, 0.5);
    border-radius: 4px;
    > .input-query-main {
      flex: 2 1 180px;
      min-width: 0;
    }
    > .input-main {
      flex: 0 1 120px;
      min-width: 0;
    }
  }
}
</style>
